import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { PageHeader } from '../../components/ui/PageHeader';
import { isStaff } from '../../lib/roles';
import type { EstadoGeneral } from '../../types/database.types';

interface FormState {
  nombre: string;
  apellido: string;
  telefono: string;
  whatsapp: string;
  email: string;
  fecha_nacimiento: string;
  estado: EstadoGeneral;
  observaciones: string;
}

const VACIO: FormState = {
  nombre: '',
  apellido: '',
  telefono: '',
  whatsapp: '',
  email: '',
  fecha_nacimiento: '',
  estado: 'activo',
  observaciones: '',
};

/**
 * Alta y edición de clientas (DAF v2, sección 6). `id === 'nueva'` marca
 * el modo creación; cualquier otro valor se trata como el uuid de una
 * clienta existente.
 *
 * Desde `schema_profesional_ventas_agenda.sql`, una profesional también
 * puede llegar aquí en modo creación (RLS: `clientes_profesional_insert`)
 * -- pero el usuario pidió explícitamente que solo pueda CREAR, no
 * editar clientas existentes (eso sigue siendo de recepción/admin). El
 * guard de abajo la saca de esta pantalla si intenta abrir el id de una
 * clienta ya existente -- sin él, el SELECT (permitido, visibilidad
 * ampliada) le cargaría igual los datos en el formulario, y solo el
 * guardado fallaría en silencio por RLS, una experiencia confusa.
 */
export function ClientaForm() {
  const { id } = useParams<{ id: string }>();
  const esNueva = id === 'nueva';
  const navigate = useNavigate();
  const { perfil } = useAuth();
  const staff = isStaff(perfil?.rol);

  const [form, setForm] = useState<FormState>(VACIO);
  const [loading, setLoading] = useState(!esNueva);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // `perfil` es `null` un instante mientras carga la sesión (ver
    // AuthContext.tsx) -- esperar a que exista antes de decidir si
    // redirigir, para no expulsar de aquí a un staff editando mientras
    // su propio perfil todavía está cargando.
    if (perfil && !esNueva && !staff) {
      navigate('/admin/clientas', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esNueva, staff, perfil]);

  useEffect(() => {
    if (esNueva || !id || !staff) return;

    supabase
      .from('clientes')
      .select('*')
      .eq('id', id)
      .single()
      .then(({ data, error: err }) => {
        if (err || !data) {
          setError(err?.message ?? 'No se encontró la clienta');
        } else {
          setForm({
            nombre: data.nombre,
            apellido: data.apellido ?? '',
            telefono: data.telefono ?? '',
            whatsapp: data.whatsapp ?? '',
            email: data.email ?? '',
            fecha_nacimiento: data.fecha_nacimiento ?? '',
            estado: data.estado,
            observaciones: data.observaciones ?? '',
          });
        }
        setLoading(false);
      });
  }, [esNueva, id]);

  function actualizar<K extends keyof FormState>(campo: K, valor: FormState[K]) {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!perfil) return;
    // useParams() tipa `id` como `string | undefined` aunque la ruta
    // siempre lo trae ('nueva' o un uuid real) -- este guard se lo
    // confirma a TypeScript para que `.eq('id', id)` más abajo compile
    // con `id: string` en vez de `string | undefined`.
    if (!id) return;
    setGuardando(true);
    setError(null);

    const payload = {
      salon_id: perfil.salon_id,
      nombre: form.nombre,
      apellido: form.apellido || null,
      telefono: form.telefono || null,
      whatsapp: form.whatsapp || null,
      email: form.email || null,
      fecha_nacimiento: form.fecha_nacimiento || null,
      estado: form.estado,
      observaciones: form.observaciones || null,
    };

    const { error: err } = esNueva
      ? await supabase.from('clientes').insert(payload)
      : await supabase.from('clientes').update(payload).eq('id', id);

    setGuardando(false);

    if (err) {
      setError(err.message);
      return;
    }

    navigate('/admin/clientas');
  }

  if (loading) return <p className="text-sm text-onyx/50">Cargando…</p>;

  return (
    <div className="max-w-2xl">
      <PageHeader title={esNueva ? 'Nueva clienta' : 'Editar clienta'} />

      <form onSubmit={handleSubmit} className="card space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Campo label="Nombre" required>
            <input
              required
              className="input-field"
              value={form.nombre}
              onChange={(e) => actualizar('nombre', e.target.value)}
            />
          </Campo>
          <Campo label="Apellido">
            <input
              className="input-field"
              value={form.apellido}
              onChange={(e) => actualizar('apellido', e.target.value)}
            />
          </Campo>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Campo label="Teléfono">
            <input
              className="input-field"
              value={form.telefono}
              onChange={(e) => actualizar('telefono', e.target.value)}
            />
          </Campo>
          <Campo label="WhatsApp">
            <input
              className="input-field"
              value={form.whatsapp}
              onChange={(e) => actualizar('whatsapp', e.target.value)}
            />
          </Campo>
        </div>

        <Campo label="Correo electrónico">
          <input
            type="email"
            className="input-field"
            value={form.email}
            onChange={(e) => actualizar('email', e.target.value)}
          />
        </Campo>

        <div className="grid grid-cols-2 gap-4">
          <Campo label="Fecha de nacimiento (opcional)">
            <input
              type="date"
              className="input-field"
              value={form.fecha_nacimiento}
              onChange={(e) => actualizar('fecha_nacimiento', e.target.value)}
            />
          </Campo>
          <Campo label="Estado">
            <select
              className="input-field"
              value={form.estado}
              onChange={(e) => actualizar('estado', e.target.value as EstadoGeneral)}
            >
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
            </select>
          </Campo>
        </div>

        <Campo label="Observaciones">
          <textarea
            className="input-field"
            rows={3}
            value={form.observaciones}
            onChange={(e) => actualizar('observaciones', e.target.value)}
          />
        </Campo>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/clientas')}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

function Campo({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-onyx/80">
        {label}
        {required && <span className="text-gold-deep"> *</span>}
      </span>
      {children}
    </label>
  );
}
